import { getSupabase } from '@/lib/supabase'
import { getPedagogyScreenName } from '@/lib/pedagogyRoutes'
import { IS_DEV_SESSION, getDevActorUserId } from '@/lib/devActor'

type RouteContext = Record<string, string | Record<string, string>>

export function getSessionId(): string {
  const KEY = 'app_session_id'
  let id = sessionStorage.getItem(KEY)
  if (!id) {
    id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem(KEY, id)
  }
  return id
}

export function getDeviceType(): 'iOS' | 'Android' | 'Web' {
  const platform = (window as any).Telegram?.WebApp?.platform ?? ''
  if (platform === 'ios') return 'iOS'
  if (platform === 'android' || platform === 'android_x') return 'Android'
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
  if (/Android/.test(ua)) return 'Android'
  return 'Web'
}

export function getAppVersion(): string {
  return (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown'
}

export function pathToScreenName(pathname: string): string | null {
  if (!pathname || pathname === '/') return 'Главная'
  if (pathname === '/partners') return 'Франчайзи'
  if (/^\/partners\/[^/]+\/analytics$/.test(pathname)) return 'Аналитика франчайзи'
  if (/^\/partners\/[^/]+\/cities\/[^/]+\/analytics$/.test(pathname)) return 'Аналитика города'
  if (/^\/partners\/[^/]+(?:\/studios\/[^/]+)?\/marketing\/daily$/.test(pathname)) return 'Маркетинг: сводка дня'
  if (/^\/partners\/[^/]+(?:\/studios\/[^/]+)?\/marketing\/plan$/.test(pathname)) return 'Маркетинг: планирование'
  if (/^\/partners\/[^/]+(?:\/studios\/[^/]+)?\/marketing$/.test(pathname)) return 'Маркетинг'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/metrics$/.test(pathname)) return 'Метрики'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/analytics$/.test(pathname)) return 'Аналитика студии'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/business-health$/.test(pathname)) return 'Здоровье бизнеса'
  if (/^\/partners\/[^/]+\/business-health$/.test(pathname)) return 'Здоровье бизнеса франчайзи'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/finance$/.test(pathname)) return 'Финансы'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/history$/.test(pathname)) return 'История'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/ratings$/.test(pathname)) return 'Рейтинги'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/trainer-reviews$/.test(pathname)) return 'Педагоги'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/teachers$/.test(pathname)) return 'Педагоги'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/teachers\/[^/]+$/.test(pathname)) return 'Карточка педагога'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/teachers\/[^/]+\/reviews\/[^/]+$/.test(pathname)) return 'Отсмотр'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/plan-history$/.test(pathname)) return 'История планов'
  if (/^\/partners\/[^/]+\/studios\/[^/]+\/studio-metrics(?:-grid)?$/.test(pathname)) return 'Студийные метрики'
  if (pathname === '/analytics') return 'Аналитика'
  if (pathname === '/rankings') return 'Рейтинги'
  if (pathname === '/management-dashboard') return 'УК Дашборд'
  if (pathname === '/management-analytics') return 'УК Аналитика'
  if (pathname === '/management/settings') return 'УК Настройки'
  if (pathname === '/management-users') return 'УК Настройки'
  if (pathname === '/management-ratings') return 'УК Единый рейтинг сети'
  if (pathname === '/management-ratings/subscriptions') return 'УК Рейтинги по подпискам'
  if (pathname === '/management/hub') return 'УК Хаб'
  if (pathname === '/management/network-planning/edit') return 'Годовой план сети'
  if (pathname === '/management/network-planning') return 'Экран планирования сети'
  if (pathname === '/management/crm-accounts') return 'CRM-аккаунты'
  if (pathname === '/management/missing-payments') return 'Аудит синхронизации платежей'
  if (pathname === '/management/original/cases') return 'Кейсы исходного'
  if (pathname === '/management/access') return 'Заявка на доступ в УК'
  if (pathname === '/management/review-sampling') return 'Выборка отсмотров'
  if (pathname === '/management/access') return 'Заявка на доступ в УК'
  if (pathname === '/management/review-sampling') return 'Выборка отсмотров'
  if (pathname === '/management/work') return 'Запросы в разработку'
  if (pathname === '/management/users') return 'Пользователи'
  if (pathname === '/management/users/auth-attempts') return 'Попытки авторизации'
  if (pathname === '/management/users/activity') return 'Активность сотрудников'
  if (pathname === '/management/employee-activity') return 'Активность сотрудников'
  if (pathname === '/management/health') return 'Здоровье системы'
  if (/\/management\/territorial-managers\/[^/]+/.test(pathname)) return 'Профиль терр. менеджера'
  if (pathname === '/management/territorial-managers') return 'Терр. менеджеры'
  if (/\/management\/personal-managers\/[^/]+/.test(pathname)) return 'Профиль перс. менеджера'
  if (pathname === '/management/personal-managers') return 'Персональные менеджеры'
  if (pathname === '/management/personal-managers-hackathon') return 'Хакатон перс. менеджеров'
  if (pathname === '/management/personal-managers-coverage') return 'Покрытие перс. менеджеров'
  if (pathname === '/management/personal-managers-control') return 'Контроль перс. менеджеров'
  if (pathname === '/management/partners-coverage') return 'Покрытие франчайзи'
  const pedagogyScreen = getPedagogyScreenName(pathname)
  if (pedagogyScreen) return pedagogyScreen
  if (/\/management\/documents-registry\/.+/.test(pathname)) return 'Документ'
  if (pathname === '/management/documents-registry') return 'Реестр документов'
  if (pathname === '/management/shmp/members') return 'ШМПО: участники'
  if (pathname === '/management/shmp') return 'ШМПО: дашборд'
  if (pathname === '/royalties/coverage-timeline') return 'Роялти: покрытие'
  if (pathname === '/royalties/process') return 'Роялти: начисления'
  if (/^\/royalties\/partner\/[^/]+$/.test(pathname)) return 'Роялти франчайзи'
  if (/^\/royalties\/series\/[^/]+$/.test(pathname)) return 'Роялти серии'
  if (/^\/royalties\/contract\/[^/]+$/.test(pathname)) return 'Роялти договора'
  if (pathname === '/royalties/import') return 'Роялти: импорт'
  if (pathname === '/royalties/allocation') return 'Роялти: разбор платежей'
  if (/\/royalties\//.test(pathname)) return 'Роялти'
  if (pathname === '/royalties') return 'Роялти: обзор'
  if (pathname === '/settings/notifications') return 'Уведомления'
  if (pathname === '/admin/notifications') return 'Уведомления: администрирование'
  if (pathname.startsWith('/invite/')) return 'Приглашение'
  return null
}

function cleanValue(value: string | null): string | null {
  if (!value) return null
  try {
    return decodeURIComponent(value).slice(0, 120)
  } catch {
    return value.slice(0, 120)
  }
}

function isSensitiveParam(key: string): boolean {
  return /token|secret|password|code|state|session/i.test(key)
}

export function sanitizePathname(pathname: string): string {
  if (pathname.startsWith('/invite/')) return '/invite/:token'
  return pathname
}

function sanitizeSearch(search: string): string {
  if (!search) return ''
  const input = new URLSearchParams(search)
  const output = new URLSearchParams()
  input.forEach((value, key) => {
    if (!isSensitiveParam(key)) output.set(key, value.slice(0, 120))
  })
  const result = output.toString()
  return result ? `?${result}` : ''
}

function collectSearchParams(search: string): Record<string, string> {
  const result: Record<string, string> = {}
  if (!search) return result
  const params = new URLSearchParams(search)
  params.forEach((value, key) => {
    if (!isSensitiveParam(key)) result[key] = value.slice(0, 120)
  })
  return result
}

function addQueryContext(context: RouteContext, search: string): RouteContext {
  const query = collectSearchParams(search)
  if (Object.keys(query).length > 0) context.query = query
  return context
}

export function buildRouteContext(pathname: string, search = ''): RouteContext {
  const context: RouteContext = {}
  const partnerAnalytics = pathname.match(/^\/partners\/([^/]+)\/analytics$/)
  const cityAnalytics = pathname.match(/^\/partners\/([^/]+)\/cities\/([^/]+)\/analytics$/)
  const studioPage = pathname.match(/^\/partners\/([^/]+)\/studios\/([^/]+)\/(.+)$/)
  const royaltiesPartner = pathname.match(/^\/royalties\/partner\/([^/]+)$/)
  const royaltiesSeries = pathname.match(/^\/royalties\/series\/([^/]+)$/)
  const royaltiesContract = pathname.match(/^\/royalties\/contract\/([^/]+)$/)
  const territorialManager = pathname.match(/^\/management\/territorial-managers\/([^/]+)$/)
  const personalManager = pathname.match(/^\/management\/personal-managers\/([^/]+)$/)
  const pedagogyTeacher = pathname.match(/^\/management\/pedagogy\/teachers\/([^/]+)$/)
  // coverage и снятый legacy-адрес capacity — не идентификаторы специалиста.
  const pedagogySpecialist = pathname.match(/^\/management\/pedagogy\/department\/(?!coverage$|capacity$)([^/]+)$/)
  const documentRegistry = pathname.match(/^\/management\/documents-registry\/([^/]+)$/)

  if (partnerAnalytics) {
    context.entity_type = 'partner'
    context.partner_number = cleanValue(partnerAnalytics[1]) ?? ''
    context.detail_label = `Франчайзи ${context.partner_number}`
  } else if (cityAnalytics) {
    context.entity_type = 'city'
    context.partner_number = cleanValue(cityAnalytics[1]) ?? ''
    context.city_id = cleanValue(cityAnalytics[2]) ?? ''
    context.detail_label = `Франчайзи ${context.partner_number}, город ${context.city_id}`
  } else if (studioPage) {
    context.entity_type = 'studio'
    context.partner_number = cleanValue(studioPage[1]) ?? ''
    context.studio_id = cleanValue(studioPage[2]) ?? ''
    context.section = cleanValue(studioPage[3]) ?? ''
    context.detail_label = `Франчайзи ${context.partner_number}, студия ${context.studio_id}`
  } else if (royaltiesPartner) {
    context.entity_type = 'royalty_partner'
    context.partner_number = cleanValue(royaltiesPartner[1]) ?? ''
    context.detail_label = `Франчайзи ${context.partner_number}`
  } else if (royaltiesSeries) {
    context.entity_type = 'royalty_series'
    context.series_id = cleanValue(royaltiesSeries[1]) ?? ''
    context.detail_label = `Серия договора ${context.series_id}`
  } else if (royaltiesContract) {
    context.entity_type = 'royalty_contract'
    context.contract_id = cleanValue(royaltiesContract[1]) ?? ''
    context.detail_label = `Договор ${context.contract_id}`
  } else if (territorialManager) {
    context.entity_type = 'territorial_manager'
    context.target_user_id = cleanValue(territorialManager[1]) ?? ''
    context.detail_label = `Пользователь ${context.target_user_id}`
  } else if (personalManager) {
    context.entity_type = 'personal_manager'
    context.target_user_id = cleanValue(personalManager[1]) ?? ''
    context.detail_label = `Пользователь ${context.target_user_id}`
  } else if (pedagogyTeacher) {
    context.entity_type = 'franchise_teacher'
    context.teacher_id = cleanValue(pedagogyTeacher[1]) ?? ''
    context.detail_label = `Педагог ${context.teacher_id}`
  } else if (pedagogySpecialist) {
    context.entity_type = 'pedagogy_specialist'
    context.target_user_id = cleanValue(pedagogySpecialist[1]) ?? ''
    context.detail_label = `Пользователь ${context.target_user_id}`
  } else if (documentRegistry) {
    context.entity_type = 'document'
    context.contract_id = cleanValue(documentRegistry[1]) ?? ''
    context.detail_label = `Документ/договор ${context.contract_id}`
  }

  return addQueryContext(context, search)
}

export function logScreenVisit(
  userId: string,
  screenName: string | null,
  durationSec: number,
  pathname?: string,
  search = ''
): void {
  if (!screenName || durationSec < 2 || durationSec > 7200) return
  const safePathname = pathname ? sanitizePathname(pathname) : null
  const safeSearch = sanitizeSearch(search)
  const extendedPayload = {
    user_id: userId,
    session_id: getSessionId(),
    screen_name: screenName,
    pathname: safePathname,
    search: safeSearch || null,
    route_context: buildRouteContext(safePathname ?? '', safeSearch),
    started_at: new Date(Date.now() - durationSec * 1000).toISOString(),
    duration_sec: durationSec,
    app_version: getAppVersion(),
    device_type: getDeviceType(),
  }
  const fallbackPayload = {
    user_id: userId,
    session_id: extendedPayload.session_id,
    screen_name: screenName,
    duration_sec: durationSec,
    app_version: extendedPayload.app_version,
    device_type: extendedPayload.device_type,
  }

  // Разметка исполнителя добавляется ТОЛЬКО локальной сборкой: в проде
  // IS_DEV_SESSION сворачивается в false, набор полей остаётся прежним, и фронт
  // не зависит от того, применена ли миграция 20260923120000.
  const devActorUserId = getDevActorUserId()
  const payloads: Array<Record<string, unknown>> = IS_DEV_SESSION
    ? [
        {
          ...extendedPayload,
          is_dev_session: true,
          // Свою же учётку актором не помечаем: это не работа под чужим именем.
          actor_user_id: devActorUserId && devActorUserId !== userId ? devActorUserId : null,
        },
        extendedPayload,
        fallbackPayload,
      ]
    : [extendedPayload, fallbackPayload]

  const insertAt = (index: number): any => {
    const payload = payloads[index]
    if (!payload) return undefined
    return (getSupabase() as any)
      .from('app_events')
      .insert(payload)
      .then(({ error }: { error: any }) => {
        if (!error) return
        const message = String(error.message ?? '')
        // PostgREST на неизвестную колонку отвечает PGRST204 «Could not find the
        // '<column>' column ... in the schema cache» — отступаем на payload попроще.
        if (!/actor_user_id|is_dev_session|pathname|search|route_context|schema cache|column/i.test(message)) return
        return insertAt(index + 1)
      })
  }

  try {
    insertAt(0)?.catch(() => undefined)
  } catch {
    // never throw
  }
}
